/**
 * Deal progress tracker: status keys and per-user-type config (order + labels).
 * Single source of truth so labels and order can change without touching the component.
 * Status values align with Favorite.investorStatus / Favorite.startupStatus (favorites collection).
 *
 * `favorited`: investor saved the startup (active favorite). Distinct from `not-contacted` (no relationship)
 * and `contacted` (outreach / conversation started).
 */

export const USER_TYPES = {
  investor: 'investor',
  startup: 'startup',
} as const;

export type UserType = (typeof USER_TYPES)[keyof typeof USER_TYPES];

/** Deal status values (shared by investor and startup, stored in favorites collection). */
export const DEAL_STATUS = {
  notContacted: 'not-contacted',
  favorited: 'favorited',
  contacted: 'contacted',
  met: 'met',
  dealProgress: 'deal-progress',
  dealSigned: 'deal-signed',
} as const;

export type DealStatus =
  | 'not-contacted'
  | 'favorited'
  | 'contacted'
  | 'met'
  | 'deal-progress'
  | 'deal-signed';

export type StepIconType = 'check' | 'current' | 'dealSigned';

export type StatusUi = {
  /** Label for current or pending step. */
  label: string;
  /** Label when step is completed (e.g. "Follow"). Falls back to label if not set. */
  labelCompleted?: string;
  /** Label when step is still pending (optional). Falls back to label if not set. */
  labelPending?: string;
  icon: StepIconType;
  /** If true, this step uses the special "Deal Signed" circle (curved text, no label below). */
  isFinalMilestone?: boolean;
};

export type TrackerConfig = {
  /** Visual steps to render (circles). Not all backend statuses need a circle. */
  order: DealStatus[];
  ui: Partial<Record<DealStatus, StatusUi>>;
};

/**
 * Canonical progression of all deal statuses, from earliest to latest.
 * Used to compute visual progress even when a status isn't in the visual order.
 */
export const DEAL_STATUS_ORDER: DealStatus[] = [
  'not-contacted',
  'favorited',
  'contacted',
  'met',
  'deal-progress',
  'deal-signed',
];

/**
 * Next status in the investor pipeline when using explicit advance (not add-to-favorites).
 * `not-contacted` → normally only resolved by having an active favorite (`favorited` in DB).
 */
export function getNextInvestorDealStatus(current: DealStatus): DealStatus | null {
  switch (current) {
    case 'not-contacted':
      return 'favorited';
    case 'favorited':
      return 'met';
    case 'contacted':
      return 'met';
    case 'met':
      return 'deal-progress';
    case 'deal-progress':
      return 'deal-signed';
    case 'deal-signed':
      return null;
    default:
      return null;
  }
}

export function getNextStartupDealStatus(current: DealStatus): DealStatus | null {
  switch (current) {
    case 'not-contacted':
      return 'favorited';
    case 'favorited':
      return 'met';
    case 'contacted':
      return 'met';
    case 'met':
      return 'deal-progress';
    case 'deal-progress':
      return 'deal-signed';
    case 'deal-signed':
      return null;
    default:
      return null;
  }
}

/** Visual step index for the progression bar, or -1 when there is no relationship yet. */
export function getDealTrackerVisualIndex(status: DealStatus, userType: UserType): number {
  const order = DEAL_TRACKER_BY_USER_TYPE[userType].order;
  if (status === 'not-contacted') return -1;
  const statusGlobalIdx = DEAL_STATUS_ORDER.indexOf(status);
  let visualIdx = -1;
  order.forEach((step, i) => {
    if (DEAL_STATUS_ORDER.indexOf(step) <= statusGlobalIdx) visualIdx = i;
  });
  return visualIdx;
}

/**
 * After adding to favorites the next circle (Contacted) stays grey (no ring).
 * Once outreach has started, the next pending circle is marked current.
 */
export function highlightDealTrackerNextStep(status: DealStatus): boolean {
  return status !== 'not-contacted' && status !== 'favorited';
}

/**
 * Green progress stops halfway on the line after the current step.
 * Includes `favorited` so Favorite → Contact is half green right after adding.
 */
export function showHalfLineAfterCurrentStep(status: DealStatus): boolean {
  return status !== 'not-contacted';
}

/** Completed earlier steps can be clicked to move the tracker back. */
export function canRewindDealTrackerToStep(
  currentStatus: DealStatus,
  targetStatus: DealStatus,
  userType: UserType
): boolean {
  const order = DEAL_TRACKER_BY_USER_TYPE[userType].order;
  const targetIndex = order.indexOf(targetStatus);
  if (targetIndex < 0) return false;
  const lastIndex = order.length - 1;
  if (targetIndex === lastIndex) return false;
  const currentIndex = getDealTrackerVisualIndex(currentStatus, userType);
  const isAllCompleted = currentStatus === order[lastIndex];
  if (isAllCompleted) return targetIndex < lastIndex;
  return targetIndex < currentIndex;
}

export const DEAL_TRACKER_BY_USER_TYPE: Record<UserType, TrackerConfig> = {
  investor: {
    order: [
      DEAL_STATUS.favorited,
      DEAL_STATUS.contacted,
      DEAL_STATUS.dealProgress,
      DEAL_STATUS.dealSigned,
    ],
    ui: {
      [DEAL_STATUS.favorited]: {
        label: 'Follow',
        labelCompleted: 'Follow',
        labelPending: 'Follow',
        icon: 'check',
      },
      [DEAL_STATUS.contacted]: {
        label: 'Contacted',
        labelCompleted: 'Contacted',
        labelPending: 'Contact Startup',
        icon: 'check',
      },
      [DEAL_STATUS.dealProgress]: {
        label: 'Negotiated deal',
        labelCompleted: 'Negotiated deal',
        labelPending: 'Negotiate Deal',
        icon: 'current',
      },
      [DEAL_STATUS.dealSigned]: {
        label: 'Deal Signed',
        icon: 'dealSigned',
        isFinalMilestone: true,
      },
    },
  },
  startup: {
    order: [
      DEAL_STATUS.favorited,
      DEAL_STATUS.contacted,
      DEAL_STATUS.met,
      DEAL_STATUS.dealProgress,
      DEAL_STATUS.dealSigned,
    ],
    ui: {
      [DEAL_STATUS.favorited]: {
        label: 'Investor Interest',
        icon: 'check',
      },
      [DEAL_STATUS.contacted]: {
        label: 'Contact with Investor',
        icon: 'check',
      },
      [DEAL_STATUS.met]: {
        label: 'Meet with them',
        icon: 'check',
      },
      [DEAL_STATUS.dealProgress]: {
        label: 'Deal in progress',
        icon: 'current',
      },
      [DEAL_STATUS.dealSigned]: {
        label: 'Deal signed',
        icon: 'dealSigned',
        isFinalMilestone: true,
      },
    },
  },
};

/** Button label for primary action at each step (null = terminal step, no action). */
export const ACTION_LABELS: Record<UserType, Partial<Record<DealStatus, string | null>>> = {
  investor: {
    [DEAL_STATUS.notContacted]: 'Follow',
    [DEAL_STATUS.favorited]: 'Message this startup',
    [DEAL_STATUS.contacted]: 'Meetings and negotiations started',
    [DEAL_STATUS.met]: 'Meetings and negotiations started',
    [DEAL_STATUS.dealProgress]: 'Deal Signed?',
    [DEAL_STATUS.dealSigned]: null,
  },
  startup: {
    [DEAL_STATUS.notContacted]: null,
    [DEAL_STATUS.favorited]: 'Contact this investor',
    [DEAL_STATUS.contacted]: "I've or we've met them",
    [DEAL_STATUS.met]: 'We have a deal in progress',
    [DEAL_STATUS.dealProgress]: 'Deal Signed?',
    [DEAL_STATUS.dealSigned]: null,
  },
};

/** Visual deal-tracker step keys shown on the investor favorites page. */
export type InvestorVisualDealStep = (typeof DEAL_TRACKER_BY_USER_TYPE.investor.order)[number];

/** Map backend favorite status to the investor progression bar step used for grouping. */
export function getInvestorVisualDealStep(status: DealStatus | undefined): InvestorVisualDealStep {
  switch (status) {
    case DEAL_STATUS.contacted:
    case DEAL_STATUS.met:
      return DEAL_STATUS.contacted;
    case DEAL_STATUS.dealProgress:
      return DEAL_STATUS.dealProgress;
    case DEAL_STATUS.dealSigned:
      return DEAL_STATUS.dealSigned;
    case DEAL_STATUS.favorited:
    case DEAL_STATUS.notContacted:
    default:
      return DEAL_STATUS.favorited;
  }
}

/** Row title for a favorites-page group (matches progression bar labels). */
export function getInvestorVisualDealStepLabel(step: InvestorVisualDealStep): string {
  const ui = DEAL_TRACKER_BY_USER_TYPE.investor.ui[step];
  return ui?.labelCompleted ?? ui?.label ?? step;
}

/** Ordered steps for rendering favorites-page category rows. */
export const INVESTOR_FAVORITES_PAGE_STEPS: InvestorVisualDealStep[] =
  DEAL_TRACKER_BY_USER_TYPE.investor.order;
